import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { tosThumbnail } from '../utils/tosImage';
import { tosAccelerate } from '../utils/tosAccelerate';
import { formatEpisodeRange } from '../utils/episodeRange';

export interface StateOption {
  state: string;
  imageUrl: string;
  episodes?: number[];
}

interface AssetStateSelectorProps {
  open: boolean;
  onClose: () => void;
  assetName: string;
  assetType?: 'character' | 'scene' | 'prop' | null;
  projectId?: string;
  states: StateOption[];
  currentState: string;
  currentImageUrl: string;
  onSelect: (state: string, imageUrl: string) => void;
  onRefresh?: () => Promise<void>;
}

const AssetStateSelector: React.FC<AssetStateSelectorProps> = ({
  open, onClose, assetName, assetType, projectId, states, currentState, currentImageUrl, onSelect, onRefresh,
}) => {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [longPressState, setLongPressState] = useState<string | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
      setSearch('');
      setLongPressState(null);
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else if (visible) {
      setExiting(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, 200);
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown, visible]);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  if (!visible) return null;

  const filtered = states.filter(s =>
    (s.state || t('assetState.defaultLabel')).toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (s: StateOption) => {
    const isSelected = s.imageUrl === currentImageUrl && s.state === currentState;
    if (isSelected) return;
    onSelect(s.state, s.imageUrl);
  };

  const handleRefresh = async () => {
    if (refreshing || !onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const handleTouchStart = (state: string) => {
    longPressTimer.current = setTimeout(() => {
      setLongPressState(state);
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'center',
    justifyContent: 'center',
    animation: exiting ? 'none' : 'fadeIn var(--transition-normal)',
    opacity: exiting ? 0 : 1,
    transition: 'opacity 0.2s',
  };

  const panel: React.CSSProperties = isMobile ? {
    background: 'var(--surface)',
    borderRadius: '16px 16px 0 0',
    padding: '1rem',
    width: '100%',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    animation: exiting ? 'none' : 'slideUp var(--transition-normal)',
    transform: exiting ? 'translateY(100%)' : 'translateY(0)',
    transition: 'transform 0.2s',
  } : {
    background: 'var(--bg-2, #f5f5f4)',
    borderRadius: '16px',
    padding: '1.5rem',
    width: '1280px',
    maxWidth: '95vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--shadow-lg)',
    animation: exiting ? 'none' : 'slideUp var(--transition-normal)',
    opacity: exiting ? 0 : 1,
    transition: 'opacity 0.2s',
  };

  const gridCols = isMobile ? 2 : 5;

  return ReactDOM.createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexShrink: 0 }}>
          {isMobile && (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
              <div style={{ width: '36px', height: '4px', background: 'var(--border)', borderRadius: '2px' }} />
            </div>
          )}
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{t('assetState.switchStateTitle', { name: assetName })}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {onRefresh && (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label={t('assetState.refreshStates')}
                title={t('assetState.refreshStates')}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: refreshing ? 'default' : 'pointer', padding: '4px 8px', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', opacity: refreshing ? 0.6 : 1 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={refreshing ? { animation: 'spin 0.8s linear infinite' } : undefined}>
                  <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', padding: '0.25rem 0.5rem', color: 'var(--text-2)' }}
              aria-label={t('closeAriaLabel')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Search - only show when 6+ states */}
        {states.length >= 6 && (
          <div style={{ marginBottom: '0.75rem', flexShrink: 0, position: 'relative' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--theme-storyboard)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder=""
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                border: 'none',
                borderRadius: 'var(--radius)',
                fontSize: '0.85rem',
                background: 'var(--surface, #fff)',
                color: 'var(--text)',
                outline: 'none',
                boxSizing: 'border-box',
                boxShadow: 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.06))',
              }}
            />
          </div>
        )}

        {/* Grid */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '4px 12px 4px 4px', margin: '-4px -12px -4px -4px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gap: '12px',
          }}>
            {filtered.map((s) => {
              const isSelected = s.imageUrl === currentImageUrl && s.state === currentState;
              const label = s.state || t('assetState.defaultLabel');
              const key = `${s.state}::${s.imageUrl}`;

              return (
                <div
                  key={key}
                  onTouchStart={() => handleTouchStart(label)}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                  style={{
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    overflow: 'hidden',
                    background: 'var(--surface, #fff)',
                    boxShadow: isSelected ? '0 0 0 2px var(--theme-storyboard), var(--shadow-xs)' : 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.05))',
                    transition: 'box-shadow 0.15s',
                    position: 'relative',
                    textAlign: 'left',
                  }}
                >
                  {/* Image — click to preview */}
                  <div
                    onClick={() => s.imageUrl && setPreviewUrl(s.imageUrl)}
                    style={{ cursor: s.imageUrl ? 'zoom-in' : 'default' }}
                  >
                    {s.imageUrl ? (
                      <img
                        src={tosThumbnail(s.imageUrl, 200)}
                        alt={label}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--surface-2)',
                        color: 'var(--warning)',
                        fontSize: '1.5rem',
                        flexDirection: 'column',
                        gap: '4px',
                      }}>
                        <span>⚠</span>
                        <span style={{ fontSize: '0.7rem' }}>{t('assetState.notGenerated')}</span>
                      </div>
                    )}
                  </div>

                  {/* 重新生成：在新标签页打开项目首页该资产并打开该状态的制作弹窗 */}
                  {projectId && assetType && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          `/projects/${projectId}?openElement=${assetType}:${encodeURIComponent(assetName)}&makeState=${encodeURIComponent(s.state)}`,
                          '_blank',
                          'noopener',
                        );
                      }}
                      style={{
                        position: 'absolute', top: '6px', left: '6px', zIndex: 2,
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        background: 'var(--theme-storyboard)', color: '#fff', border: 'none',
                        borderRadius: '6px', padding: '3px 9px', fontSize: '0.72rem', fontWeight: 600,
                        cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      }}
                      title={t('assetState.edit')}
                    >
                      {t('assetState.edit')}
                    </button>
                  )}

                  {/* Select circle — always visible */}
                  <div
                    onClick={(e) => { e.stopPropagation(); handleSelect(s); }}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: isSelected ? 'var(--theme-storyboard)' : 'rgba(255,255,255,0.85)',
                      border: isSelected ? 'none' : '2px solid var(--border)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {isSelected && '✓'}
                  </div>

                  {/* Label */}
                  <div
                    title={label}
                    style={{
                      padding: '6px 8px',
                      fontSize: '0.75rem',
                      lineHeight: '1.4',
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                    {!s.imageUrl && <span style={{ color: 'var(--warning)', marginLeft: '4px' }}>({t('assetState.notGenerated')})</span>}
                  </div>

                  {/* Episode range */}
                  <div style={{
                    padding: '2px 8px 6px',
                    fontSize: '0.65rem',
                    lineHeight: '1.3',
                    color: 'var(--text-3)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                    title={s.episodes && s.episodes.length > 0 ? formatEpisodeRange(s.episodes) : ''}
                  >
                    {s.episodes && s.episodes.length > 0 ? formatEpisodeRange(s.episodes) : '—'}
                  </div>
                </div>
              );
            })}
            {projectId && assetType && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/projects/${projectId}?openElement=${assetType}:${encodeURIComponent(assetName)}&addState=1`,
                    '_blank',
                    'noopener',
                  );
                }}
                title={t('assetState.addState')}
                style={{
                  border: '1px dashed var(--border)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--surface, #fff)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  minHeight: '120px',
                  color: 'var(--text-3)',
                }}
              >
                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>+</span>
                <span style={{ fontSize: '0.8rem' }}>{t('assetState.addState')}</span>
              </button>
            )}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)', fontSize: '0.85rem' }}>
              {t('assetState.noMatch')}
            </div>
          )}
        </div>

        {/* Mobile long-press tooltip */}
        {isMobile && longPressState && (
          <div
            onClick={() => setLongPressState(null)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.5)',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius)',
                padding: '1rem 1.25rem',
                maxWidth: '85vw',
                fontSize: '0.85rem',
                lineHeight: 1.5,
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              {longPressState}
            </div>
          </div>
        )}
      </div>
      {previewUrl && (
        <div
          onClick={(e) => { e.stopPropagation(); setPreviewUrl(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <img src={tosAccelerate(previewUrl)} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }} />
        </div>
      )}
    </div>,
    document.body
  );
};

export default AssetStateSelector;
